import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import {
  evaluateAudienceZone,
  evaluateAudienceTruthyAggregate,
  toggleAudienceEndpoint,
  toggleAudienceSync,
} from '@go-shared/lib/audience';
import { Audience } from '@go-shared/types/audience';
import React from 'react';
import { BubbleButton, BubbleInput, BubbleTable } from './BubbleUI';
import {
  BubbleTableFieldBehavior,
  BubbleTableFields,
  BubbleTableTemplate,
} from './BubbleUI/BubbleTable';

interface AudienceTableItem {
  key: string;
  label: string;
  visible: boolean | null;
}

interface AudienceVisibleTableProps {
  isTemplate: boolean;
  readOnly?: boolean;
  sourceAud: Audience | undefined;
  parentAud: Audience | undefined;
  onUpdateAudience?: (newAud: Audience) => void;
}

export default function AudienceVisibilityTable(props: AudienceVisibleTableProps) {
  const { activeProperty: property } = useActiveProperty();
  const { isTemplate, sourceAud, parentAud, onUpdateAudience } = props;
  const zones = property.zones;
  const zoneIds = zones.map(z => z._id);

  const visibilityTableItems: AudienceTableItem[] = [
    {
      key: 'all',
      label: 'All',
      visible: evaluateAudienceTruthyAggregate('visible', zoneIds, sourceAud, parentAud),
    },
    ...zones.map(z => ({
      key: z._id,
      label: z.name,
      visible: evaluateAudienceZone('visible', z._id, sourceAud, parentAud),
    })),
  ];
  const visibilityTableFields: BubbleTableFields<AudienceTableItem>[] = [
    { key: 'actions', behavior: BubbleTableFieldBehavior.Condense },
    { key: 'key' },
  ];
  const visibilityTableTemplates: BubbleTableTemplate<AudienceTableItem>[] = [
    {
      key: 'key',
      render(item) {
        return <div>{item.label}</div>;
      },
    },
    {
      key: 'actions',
      render(item) {
        return (
          <BubbleButton
            variant={item.visible ? 'navy' : item.visible === null ? 'banana' : 'smoke'}
            inverse
            className="p-1-i"
            onClick={() => {
              if (onUpdateAudience) {
                onUpdateAudience(
                  toggleAudienceEndpoint('visible', item.key, zoneIds, sourceAud, parentAud)
                );
              }
            }}
            icon={item.visible === true ? 'eye' : item.visible === false ? 'eye-slash' : 'eye'}
          />
        );
      },
    },
  ];

  return (
    <fieldset disabled={props.readOnly}>
      {!isTemplate && (
        <div className="flex items-center mb-3 relative">
          <BubbleInput
            type="checkbox"
            inputClassName="stretched-link"
            checked={sourceAud?.syncWithTemplate ?? true}
            onValue={() => {
              if (onUpdateAudience) {
                onUpdateAudience(toggleAudienceSync(sourceAud));
              }
            }}
          />
          <span className="ml-1">Default to Library</span>
        </div>
      )}
      <BubbleTable
        slim
        responsive
        hideHeader
        striped
        bordered
        headVariant="navy"
        stickyHeader={0}
        items={visibilityTableItems}
        templates={visibilityTableTemplates}
        fields={visibilityTableFields}
      />
    </fieldset>
  );
}
